export const M5_UNIT_11 = {
  id: "m5-u11-job-application",
  title: "Unit 11: Getting a Job",
  topic: "Job applications, interviews, workplace vocabulary, and indefinite pronouns",
  iconType: "briefcase",
  grammarImage: "/grammar/grammar-m5-u11.jpg",
  grammarText: "This unit teaches you essential job application English. You'll learn vocabulary for the hiring process (look for a job, apply, interview, get the job), how to read job advertisements, and how to answer interview questions. You'll also master tricky grammar: subject vs object questions (Who called? vs Who did you call?) and indefinite pronouns (someone, anyone, everyone, no one) used in workplace contexts. This combines practical vocabulary with advanced grammar patterns (B1 level) that are crucial for professional communication, job interviews, and understanding workplace conversations.",

  sets: [
    // ===================================================================
    // SET 1: JOB SEARCH VOCABULARY (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u11-s1",
      title: "Set 1: Looking for a Job",
      questions: [
        {
          type: "match",
          q: "Match job search steps in order:",
          pairs: [
            { left: "1. Look for a job", right: "Search for positions" },
            { left: "2. Apply for a job", right: "Send resume/CV" },
            { left: "3. Have an interview", right: "Meet the employer" }
          ],
          a: "completed",
          successtip: "✓ Job process: Look → Apply → Interview → Get → Start"
        },
        {
          type: "mc",
          q: "PROCESS: 'I need work. I'm ___ a job right now.'",
          options: ["looking for", "applying for", "starting"],
          a: "looking for",
          tip: "'Look for' = search for (buscar)"
        },
        {
          type: "input",
          q: "APPLICATION: 'I found a good position. I will ___ for it tomorrow.' (send documents)",
          a: "apply",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Apply for a job = send your resume/CV"
        },
        {
          type: "order",
          q: "Ordena: interview / have / Did / an / you / ?",
          words: ["Did", "you", "have", "an", "interview?"],
          a: "Did you have an interview?"
        },
        {
          type: "match",
          q: "Match US/UK vocabulary:",
          pairs: [
            { left: "Resume (US)", right: "CV/Curriculum Vitae (UK)" },
            { left: "Both mean", right: "Document with your experience" },
            { left: "Apply", right: "Same in US and UK" }
          ],
          a: "completed",
          successtip: "✓ Resume (US) = CV (UK). Both list your qualifications and experience"
        },
        {
          type: "mc",
          q: "SUCCESS: 'Great news! I ___ the job! I start Monday!'",
          options: ["got", "applied", "looked"],
          a: "got",
          tip: "'Got the job' = they hired you (te contrataron)",
          successtip: "✓ Get the job (hired) ≠ Start the job (first day of work)"
        },
        {
          type: "input",
          q: "FIRST DAY: 'I got the job last week. I ___ next Monday.' (begin working)",
          a: "start",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["begin"] }
        },
        {
          type: "order",
          q: "Traduce: Estoy buscando trabajo en marketing.",
          words: ["I'm", "looking", "for", "a", "job", "in", "marketing"],
          a: "I'm looking for a job in marketing"
        },
        {
          type: "mc",
          q: "COMPLETE SEQUENCE: 'First I applied for the job. Then I ___. Finally I got the job!'",
          options: ["had an interview", "looked for a job", "started the job"],
          a: "had an interview",
          tip: "Logical order: apply → interview → get job"
        },
        {
          type: "input",
          q: "DOCUMENT: 'I need to send my ___ with my work experience and education.' (US term)",
          a: "resume",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["CV", "curriculum vitae"] },
          successtip: "✓ Resume/CV includes: education, experience, skills, references"
        }
      ]
    },

    // ===================================================================
    // SET 2: RESUME/CV VOCABULARY (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u11-s2",
      title: "Set 2: Your Resume",
      questions: [
        {
          type: "match",
          q: "Match resume sections:",
          pairs: [
            { left: "Qualification", right: "Degree, certificate, diploma" },
            { left: "Work experience", right: "Previous jobs and duties" },
            { left: "Reference", right: "Person who recommends you" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "EDUCATION: 'My ___ include a degree in Business and a marketing certificate.'",
          options: ["qualifications", "experiences", "references"],
          a: "qualifications",
          tip: "Qualifications = educational achievements (títulos, certificados)"
        },
        {
          type: "input",
          q: "PAST JOBS: 'I have five years of work ___ in customer service.' (job history)",
          a: "experience",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Experience = what you did in previous jobs"
        },
        {
          type: "order",
          q: "Ordena professional sentence:",
          words: ["I", "need", "a", "reference", "from", "my", "last", "boss"],
          a: "I need a reference from my last boss",
          successtip: "✓ Reference = recommendation letter from previous employer/professor"
        },
        {
          type: "match",
          q: "Match personal info on resume:",
          pairs: [
            { left: "Hobby", right: "What you do for fun" },
            { left: "Interest", right: "Topics you're passionate about" },
            { left: "Skills", right: "What you can do well" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PERSONAL: 'My ___ include photography and hiking. I love the outdoors!'",
          options: ["hobbies", "qualifications", "references"],
          a: "hobbies",
          tip: "Hobbies = leisure activities (pasatiempos)"
        },
        {
          type: "input",
          q: "PASSION: 'I have a strong ___ in technology and innovation.' (passion word)",
          a: "interest",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ Interest (singular) or interests (plural). 'Interested IN' (preposition)"
        },
        {
          type: "mc",
          q: "ASKING FOR REFERENCE: 'Can you give me a reference?' means:",
          options: ["Write a recommendation letter", "Give me a job", "Pay me more"],
          a: "Write a recommendation letter",
          tip: "Reference = letter saying you're a good worker/student"
        },
        {
          type: "input",
          q: "COMPLETE: 'My previous boss ___ me an excellent reference.' (past of give)",
          a: "gave",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Give → gave (irregular)"
        },
        {
          type: "order",
          q: "Traduce: Tengo una licenciatura en informática.",
          words: ["I", "have", "a", "degree", "in", "computer", "science"],
          a: "I have a degree in computer science",
          successtip: "✓ Degree IN [subject]. Bachelor's degree, Master's degree, PhD"
        }
      ]
    },

    // ===================================================================
    // SET 3: READING JOB ADS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u11-s3",
      title: "Set 3: Job Advertisements",
      questions: [
        {
          type: "match",
          q: "Match job ad vocabulary:",
          pairs: [
            { left: "Part-time", right: "Less than 40 hours/week" },
            { left: "Full-time", right: "Usually 35-40 hours/week" },
            { left: "Experience needed", right: "Must have done it before" }
          ],
          a: "completed",
          successtip: "✓ Part-time vs Full-time. Internship = práctica profesional"
        },
        {
          type: "mc",
          q: "READ AD: 'WANTED: Teacher. Part-time, 20 hours per week.' How many hours?",
          options: ["20 hours", "40 hours", "Full week"],
          a: "20 hours",
          tip: "Part-time = less than full-time (usually less than 35-40 hours)"
        },
        {
          type: "input",
          q: "AD SAYS: 'Salary: $15 per hour.' What do they pay?",
          a: "15",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["$15", "15 dollars", "fifteen dollars"] },
          tip: "Per hour = por hora. Annual salary = salario anual"
        },
        {
          type: "order",
          q: "Build job requirement:",
          words: ["Experience", "needed", "in", "customer", "service"],
          a: "Experience needed in customer service"
        },
        {
          type: "mc",
          q: "JOB AD: 'Assistant chef needed. Saturdays and Sundays, 6am-2pm.' When do they work?",
          options: ["Weekends", "Weekdays", "Every day"],
          a: "Weekends",
          tip: "Saturday + Sunday = weekend"
        },
        {
          type: "input",
          q: "REQUIREMENT: 'Some experience ___.' What's missing? (required/needed)",
          a: "needed",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["required", "necessary"] }
        },
        {
          type: "match",
          q: "Match workplace terms:",
          pairs: [
            { left: "Salary/Pay", right: "Money you earn" },
            { left: "Benefits", right: "Health insurance, vacation days" },
            { left: "Schedule", right: "Work hours/days" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "READ: 'Gardener needed at college. Part-time. 20 hours per week. Experience needed.' Is it full-time?",
          options: ["No, part-time", "Yes, full-time", "Not specified"],
          a: "No, part-time",
          tip: "Ad clearly says 'Part-time' and '20 hours' (not 40)"
        },
        {
          type: "input",
          q: "LOCATION: 'Job in Ohio' means the job is ___. (where)",
          a: "in Ohio",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Ohio", "located in Ohio"] }
        },
        {
          type: "order",
          q: "Translate job ad line: Se necesita experiencia en ventas.",
          words: ["Experience", "needed", "in", "sales"],
          a: "Experience needed in sales",
          successtip: "✓ 'Experience needed IN [field]' = common job ad phrase"
        }
      ]
    },

    // ===================================================================
    // SET 4: WORKPLACE VOCABULARY (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u11-s4",
      title: "Set 4: At the Workplace",
      questions: [
        {
          type: "match",
          q: "Match workplace people:",
          pairs: [
            { left: "Boss", right: "Person in charge of you" },
            { left: "Manager", right: "Runs a department/team" },
            { left: "Staff", right: "All the employees" }
          ],
          a: "completed",
          successtip: "✓ Boss = your direct supervisor. Manager = departmental leader. CEO = top boss"
        },
        {
          type: "mc",
          q: "HIERARCHY: 'My ___ told me I'm doing great work. She's in charge of our team.'",
          options: ["manager", "customer", "staff"],
          a: "manager",
          tip: "Manager = person who manages a team/department"
        },
        {
          type: "input",
          q: "CLIENTS: 'I work in customer service. I talk to ___ every day.' (people who buy)",
          a: "customers",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["clients"] },
          successtip: "✓ Customer = client (both mean people who buy/use your service)"
        },
        {
          type: "order",
          q: "Ordena: staff / The / a / wants / raise / salary",
          words: ["The", "staff", "wants", "a", "salary", "raise"],
          a: "The staff wants a salary raise",
          successtip: "✓ Salary raise = aumento de sueldo"
        },
        {
          type: "match",
          q: "Match job types:",
          pairs: [
            { left: "Nine-to-five job", right: "Standard office hours" },
            { left: "Part-time", right: "Fewer hours than full-time" },
            { left: "Full-time", right: "Standard 35-40 hours/week" }
          ],
          a: "completed",
          successtip: "✓ 'Nine-to-five' = 9am-5pm = typical office hours"
        },
        {
          type: "mc",
          q: "PAYMENT: 'I want to ask for a higher ___. I need more money!'",
          options: ["salary", "customer", "staff"],
          a: "salary",
          tip: "Salary = pay = sueldo"
        },
        {
          type: "input",
          q: "COMPANY: 'I work for a tech ___. We develop apps.' (organization)",
          a: "company",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["firm", "business"] }
        },
        {
          type: "mc",
          q: "OFFICE CULTURE: 'All the ___ are going to lunch together to celebrate.'",
          options: ["staff", "boss", "salary"],
          a: "staff",
          tip: "Staff = employees (plural group, but uses singular verb)",
          successtip: "✓ 'The staff IS' (singular verb) even though staff = group of people"
        },
        {
          type: "input",
          q: "SCHEDULE: 'I work ___ from 9am to 5pm, Monday to Friday.' (time expression)",
          a: "nine-to-five",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["9 to 5", "9-5", "nine to five", "full-time"] }
        },
        {
          type: "order",
          q: "Traduce: Mi jefe quiere una reunión mañana.",
          words: ["My", "boss", "wants", "a", "meeting", "tomorrow"],
          a: "My boss wants a meeting tomorrow"
        }
      ]
    },

    // ===================================================================
    // SET 5: INTERVIEW QUESTIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u11-s5",
      title: "Set 5: Common Interview Questions",
      questions: [
        {
          type: "match",
          q: "Match interview questions to their purpose:",
          pairs: [
            { left: "Why do you want this job?", right: "Motivation" },
            { left: "What's your experience?", right: "Background" },
            { left: "When can you start?", right: "Availability" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "INTERVIEWER: 'Why do you want this job?' Best answer:",
          options: ["I love the company and I'm passionate about this field", "I need money", "I don't know"],
          a: "I love the company and I'm passionate about this field",
          tip: "Show enthusiasm and interest in the role/company",
          successtip: "✓ Avoid: 'I need money.' Focus on: passion, skills, company mission"
        },
        {
          type: "input",
          q: "EXPERIENCE QUESTION: 'What did you do at your last job?' 'I ___ with customers and solved problems.' (work)",
          a: "worked",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["dealt", "helped"] },
          tip: "Past simple for previous job"
        },
        {
          type: "order",
          q: "Ordena professional response:",
          words: ["I", "have", "three", "years", "of", "experience", "in", "sales"],
          a: "I have three years of experience in sales"
        },
        {
          type: "mc",
          q: "INTERVIEWER: 'When can you start?' You're available immediately. Say:",
          options: ["I can start immediately", "I started yesterday", "I will starting soon"],
          a: "I can start immediately",
          tip: "'Can start' = availability. Immediately = right away",
          successtip: "✓ Or: 'I can start next week' / 'I need two weeks' notice'"
        },
        {
          type: "input",
          q: "SKILLS: 'Are you good ___ working with people?' (preposition)",
          a: "at",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Good AT' + -ing verb",
          successtip: "✓ Pattern: 'good AT' + [skill/gerund]. 'I'm good at solving problems'"
        },
        {
          type: "match",
          q: "Match question to answer type:",
          pairs: [
            { left: "Why did you leave?", right: "Past reason" },
            { left: "What are your strengths?", right: "Skills" },
            { left: "Where do you see yourself in 5 years?", right: "Future goals" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "LEAVING PREVIOUS JOB: 'Why did you leave your last job?' Professional answer:",
          options: ["I wanted new challenges and growth opportunities", "I hated my boss", "The job was boring"],
          a: "I wanted new challenges and growth opportunities",
          tip: "Stay positive! Don't criticize previous employers",
          successtip: "✓ Good reasons: seeking growth, new challenges, better fit, relocation"
        },
        {
          type: "input",
          q: "STRENGTHS: 'What are your strengths?' 'I'm very organized and I work ___ under pressure.' (good)",
          a: "well",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Work well' (adverb), not 'work good'"
        },
        {
          type: "order",
          q: "Traduce: ¿Por qué quieres trabajar aquí?",
          words: ["Why", "do", "you", "want", "to", "work", "here?"],
          a: "Why do you want to work here?"
        },
        {
          type: "mc",
          q: "CLOSING: Interviewer says 'Do you have any questions for us?' Best response:",
          options: ["Yes, what are the growth opportunities here?", "No", "How much vacation do I get?"],
          a: "Yes, what are the growth opportunities here?",
          tip: "Always ask a question! Shows interest",
          successtip: "✓ Good questions: team culture, growth, training, typical day. Avoid: salary/vacation first"
        }
      ]
    },

    // ===================================================================
    // SET 6: SUBJECT VS OBJECT QUESTIONS - INTRO (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u11-s6",
      title: "Set 6: Who Questions (Tricky!)",
      questions: [
        {
          type: "match",
          q: "Two types of WHO questions:",
          pairs: [
            { left: "Who called?", right: "Subject question (no 'did')" },
            { left: "Who did you call?", right: "Object question (uses 'did')" },
            { left: "Different grammar!", right: "Different meanings!" }
          ],
          a: "completed",
          successtip: "✓ This is TRICKY! Subject ≠ Object questions have different structures"
        },
        {
          type: "mc",
          q: "STATEMENT: 'John called Mary.' QUESTION about John (who did the action):",
          options: ["Who called Mary?", "Who did John call?", "Who did call Mary?"],
          a: "Who called Mary?",
          tip: "Subject question = who DID the action → no 'did'",
          successtip: "✓ Subject question: Who called? (no 'did'). Object: Who did you call? (uses 'did')"
        },
        {
          type: "input",
          q: "STATEMENT: 'John called Mary.' QUESTION about Mary (who received the action): 'Who ___ John call?'",
          a: "did",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Object question = who RECEIVED action → uses 'did'"
        },
        {
          type: "order",
          q: "Build SUBJECT question (who did it):",
          words: ["Who", "sent", "this", "email?"],
          a: "Who sent this email?",
          successtip: "✓ Subject: Who sent? Who called? Who wrote? (NO 'did')"
        },
        {
          type: "order",
          q: "Build OBJECT question (who received it):",
          words: ["Who", "did", "you", "email?"],
          a: "Who did you email?",
          successtip: "✓ Object: Who did you email? Who did you call? Who did you see? (USES 'did')"
        },
        {
          type: "mc",
          q: "WORKPLACE: 'The manager called the staff.' Question about who called:",
          options: ["Who called the staff?", "Who did the manager call?", "Who did call?"],
          a: "Who called the staff?",
          tip: "Who = subject = did the action → no 'did'"
        },
        {
          type: "input",
          q: "WORKPLACE: 'The manager called the staff.' Question about who received call: 'Who did the manager ___?' (base verb)",
          a: "call",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "After 'did', use base form (not 'called')"
        },
        {
          type: "mc",
          q: "IDENTIFY TYPE: 'Who hired you?' This is a ___ question.",
          options: ["Subject (no 'did')", "Object (uses 'did')", "Not a question"],
          a: "Subject (no 'did')",
          tip: "No 'did' in question → subject question"
        },
        {
          type: "mc",
          q: "IDENTIFY TYPE: 'Who did you hire?' This is a ___ question.",
          options: ["Object (uses 'did')", "Subject (no 'did')", "Not a question"],
          a: "Object (uses 'did')",
          tip: "Uses 'did' → object question"
        },
        {
          type: "input",
          q: "PRACTICE: 'Someone gave me a reference.' Question: 'Who ___ you a reference?' (past of give)",
          a: "gave",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Subject question → no 'did' → use past simple (gave)",
          successtip: "✓ Subject: Who gave? (past verb). Object: Who did you give...to? (base verb)"
        }
      ]
    },

    // ===================================================================
    // SET 7: SUBJECT QUESTIONS PRACTICE (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u11-s7",
      title: "Set 7: Subject Questions",
      questions: [
        {
          type: "match",
          q: "Subject question pattern:",
          pairs: [
            { left: "Structure", right: "Who + past verb + object" },
            { left: "Example", right: "Who called the boss?" },
            { left: "NO 'did'!", right: "Use past simple verb directly" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "OFFICE: 'Who ___ the meeting?' (organized - past)",
          options: ["organized", "did organize", "organize"],
          a: "organized",
          tip: "Subject question → past simple (no 'did')"
        },
        {
          type: "input",
          q: "EMAIL: 'Who ___ this letter last week?' (send - past)",
          a: "sent",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Send → sent (irregular)"
        },
        {
          type: "order",
          q: "Build subject question:",
          words: ["Who", "got", "the", "promotion?"],
          a: "Who got the promotion?",
          successtip: "✓ Promotion = ascenso/promoción (better job position)"
        },
        {
          type: "mc",
          q: "HIRING: 'Who ___ the new manager?' (hire - past)",
          options: ["hired", "did hire", "hires"],
          a: "hired",
          tip: "Subject question uses past simple directly"
        },
        {
          type: "input",
          q: "MISTAKE: 'Who ___ my coffee?' (drink - past)",
          a: "drank",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Drink → drank (irregular)"
        },
        {
          type: "match",
          q: "Match subject questions to contexts:",
          pairs: [
            { left: "Who broke the printer?", right: "Finding out who did something" },
            { left: "Who started the meeting?", right: "Identifying the person responsible" },
            { left: "Who wrote this report?", right: "Asking about authorship" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "RESPONSIBILITY: 'Who closed the office last night?' Answer:",
          options: ["I did", "Me", "I closed"],
          a: "I did",
          tip: "Short answer to subject question: 'I did' / 'John did'",
          successtip: "✓ Short answers: 'I did' / 'Sarah did' / 'The manager did'"
        },
        {
          type: "input",
          q: "PRESENT TENSE SUBJECT: 'Who ___ in this office?' (work - present)",
          a: "works",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Present subject question: Who + present verb (no 'does')",
          successtip: "✓ Present: Who works? Who wants? (no 'does'). Past: Who worked? (no 'did')"
        },
        {
          type: "order",
          q: "Traduce: ¿Quién escribió este email?",
          words: ["Who", "wrote", "this", "email?"],
          a: "Who wrote this email?",
          successtip: "✓ Write → wrote (irregular). Subject question (no 'did')"
        },
        {
          type: "mc",
          q: "WHAT subject question: '___ broke?' (what = subject)",
          options: ["What broke?", "What did break?", "What break?"],
          a: "What broke?",
          tip: "WHAT can also be subject! Same rule: no 'did'",
          successtip: "✓ Subject questions work with: Who, What, Which. 'What happened?' 'Which broke?'"
        }
      ]
    },

    // ===================================================================
    // SET 8: OBJECT QUESTIONS PRACTICE (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u11-s8",
      title: "Set 8: Object Questions",
      questions: [
        {
          type: "match",
          q: "Object question pattern:",
          pairs: [
            { left: "Structure", right: "Who + did + subject + base verb" },
            { left: "Example", right: "Who did you call?" },
            { left: "USES 'did'!", right: "Base form after 'did'" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "MEETING: 'Who did the manager ___?' (invite)",
          options: ["invite", "invited", "invites"],
          a: "invite",
          tip: "Object question: after 'did', use BASE FORM"
        },
        {
          type: "input",
          q: "INTERVIEW: 'Who did you ___ at the office yesterday?' (see - base)",
          a: "see",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "After 'did', always base form (not 'saw'!)"
        },
        {
          type: "order",
          q: "Build object question:",
          words: ["Who", "did", "she", "hire?"],
          a: "Who did she hire?"
        },
        {
          type: "mc",
          q: "PHONE: 'Who did you call this morning?' Answer:",
          options: ["I called my boss", "My boss", "Both are correct"],
          a: "Both are correct",
          tip: "Can answer with full sentence or just the person",
          successtip: "✓ Short: 'My boss.' / Full: 'I called my boss.' Both work!"
        },
        {
          type: "input",
          q: "EMAIL: 'Who did you ___ the report to?' (send - base)",
          a: "send",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match question words in object questions:",
          pairs: [
            { left: "Who did you see?", right: "Person" },
            { left: "What did you eat?", right: "Thing" },
            { left: "Which did you choose?", right: "Selection" }
          ],
          a: "completed",
          successtip: "✓ Object questions: Who/What/Which + did + subject + base verb"
        },
        {
          type: "mc",
          q: "WHAT OBJECT: 'What did she ___?' (say - base)",
          options: ["say", "said", "says"],
          a: "say",
          tip: "After 'did', base form (not 'said')"
        },
        {
          type: "input",
          q: "WHICH OBJECT: 'Which candidate did they ___?' (hire - base)",
          a: "hire",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Traduce: ¿A quién viste en la oficina?",
          words: ["Who", "did", "you", "see", "at", "the", "office?"],
          a: "Who did you see at the office?"
        },
        {
          type: "mc",
          q: "COMPARE: 'Who broke the printer?' vs 'Who did you call?' Which needs 'did'?",
          options: ["Second one (object)", "First one (subject)", "Both", "Neither"],
          a: "Second one (object)",
          tip: "Subject = no 'did'. Object = uses 'did'",
          successtip: "✓ Subject: Who broke? Object: Who did you call? Now you know the difference!"
        }
      ]
    },

    // ===================================================================
    // SET 9: INDEFINITE PRONOUNS - SOMEONE/ANYONE (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u11-s9",
      title: "Set 9: Someone & Anyone",
      questions: [
        {
          type: "match",
          q: "Match indefinite pronouns:",
          pairs: [
            { left: "Someone/Somebody", right: "Positive statements" },
            { left: "Anyone/Anybody", right: "Questions & negatives" },
            { left: "Both mean", right: "A person (identity unknown)" }
          ],
          a: "completed",
          successtip: "✓ Someone = somebody. Anyone = anybody. Interchangeable!"
        },
        {
          type: "mc",
          q: "POSITIVE: '___ called you this morning.' (unknown person)",
          options: ["Someone", "Anyone", "Everyone"],
          a: "Someone",
          tip: "Positive statement → someone/somebody"
        },
        {
          type: "input",
          q: "QUESTION: 'Did ___ leave a message?' (unknown person)",
          a: "anyone",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["anybody"] },
          tip: "Question → anyone/anybody",
          successtip: "✓ Questions & negatives use: anyone, anything, anywhere"
        },
        {
          type: "order",
          q: "Build office question:",
          words: ["Is", "anyone", "in", "the", "office?"],
          a: "Is anyone in the office?"
        },
        {
          type: "mc",
          q: "NEGATIVE: 'I didn't see ___ at reception.'",
          options: ["anyone", "someone", "everyone"],
          a: "anyone",
          tip: "Negative (didn't) → anyone"
        },
        {
          type: "input",
          q: "POSITIVE RESPONSE: 'Yes, ___ is waiting for you in the lobby.' (person)",
          a: "someone",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["somebody"] }
        },
        {
          type: "match",
          q: "Pattern recognition:",
          pairs: [
            { left: "Someone is here", right: "Positive" },
            { left: "Is anyone here?", right: "Question" },
            { left: "No one is here", right: "Negative meaning" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "REQUEST: 'Can ___ help me with this printer?'",
          options: ["anyone", "someone", "both work"],
          a: "both work",
          tip: "Requests/offers can use someone OR anyone",
          successtip: "✓ Requests: 'Can anyone help?' or 'Can someone help?' Both OK!"
        },
        {
          type: "input",
          q: "OFFICE: 'Please ask ___ to call me back.' (any person)",
          a: "someone",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["somebody"] },
          tip: "Positive request → someone (though anyone also works)"
        },
        {
          type: "order",
          q: "Traduce: ¿Alguien llamó mientras estaba fuera?",
          words: ["Did", "anyone", "call", "while", "I", "was", "out?"],
          a: "Did anyone call while I was out?"
        },
        {
          type: "mc",
          q: "WORKPLACE: '___ left their laptop in the meeting room.'",
          options: ["Someone", "Anyone", "Everyone"],
          a: "Someone",
          tip: "Positive statement → someone"
        }
      ]
    },

    // ===================================================================
    // SET 10: INDEFINITE PRONOUNS - EVERYONE/NO ONE (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u11-s10",
      title: "Set 10: Everyone & No One",
      questions: [
        {
          type: "match",
          q: "Match group pronouns:",
          pairs: [
            { left: "Everyone/Everybody", right: "All people in the group" },
            { left: "No one/Nobody", right: "Zero people (none)" },
            { left: "Important!", right: "Use SINGULAR verb" }
          ],
          a: "completed",
          successtip: "✓ Everyone IS (not 'are'). No one IS (not 'are'). Singular verb!"
        },
        {
          type: "mc",
          q: "MEETING: '___ is going to the conference tomorrow.' (all people)",
          options: ["Everyone", "Anyone", "Someone"],
          a: "Everyone",
          tip: "All people → everyone/everybody"
        },
        {
          type: "input",
          q: "VERB AGREEMENT: 'Everyone ___ at the meeting.' (be - present)",
          a: "is",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Everyone = singular → 'is' (not 'are'!)",
          successtip: "✓ TRICKY! Everyone/everybody = singular. 'Everyone IS here' (not 'are')"
        },
        {
          type: "order",
          q: "Build office statement:",
          words: ["No", "one", "is", "in", "the", "office"],
          a: "No one is in the office",
          successtip: "✓ 'No one' = two words (not 'noone'). Nobody = one word"
        },
        {
          type: "mc",
          q: "EMPTY: 'Why is ___ here? Where is everyone?'",
          options: ["no one", "everyone", "someone"],
          a: "no one",
          tip: "Zero people → no one/nobody"
        },
        {
          type: "input",
          q: "ANNOUNCEMENT: 'Everybody ___ a raise this year!' (get - past)",
          a: "got",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["gets"] },
          tip: "Everybody = everyone (same thing)"
        },
        {
          type: "match",
          q: "Match office scenarios:",
          pairs: [
            { left: "Everyone knows the password", right: "All staff have it" },
            { left: "No one knows the password", right: "Nobody has it" },
            { left: "Someone knows the password", right: "At least one person has it" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "CELEBRATION: 'Everyone ___ going to the party tonight!'",
          options: ["is", "are", "were"],
          a: "is",
          tip: "Everyone = singular verb (is/was)",
          successtip: "✓ Present: Everyone IS. Past: Everyone WAS. Future: Everyone WILL BE"
        },
        {
          type: "input",
          q: "NEGATIVE: '___ answered the phone. The office was closed.' (zero people)",
          a: "No one",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Nobody"] },
          successtip: "✓ No one = Nobody = not a single person"
        },
        {
          type: "order",
          q: "Traduce: Todos quieren un aumento de salario.",
          words: ["Everyone", "wants", "a", "salary", "raise"],
          a: "Everyone wants a salary raise",
          successtip: "✓ Everyone wants (singular 's'!). 'All people want' would use plural"
        },
        {
          type: "mc",
          q: "FINAL: Complete office scenario: '___ was at the meeting, so ___ could answer my question.'",
          options: ["No one / no one", "Everyone / someone", "Someone / anyone"],
          a: "No one / no one",
          tip: "Both blanks need 'zero people' meaning",
          successtip: "✓ MASTERED! Someone (positive), Anyone (question/negative), Everyone (all), No one (none)"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - Job Interview Transcript (10 questions)
  // ===================================================================
  // ===================================================================
// READING SET: Job Interview
// ===================================================================
readingSet: {
  id: "m5-u11-reading",
  title: "Reading: Job Interview",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Arrival & Greetings (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Arrival**

Sarah Chen arrived at the office of Tech Solutions Inc. for her job interview. The receptionist smiled and said, "Hello! **Someone** from HR will be with you shortly. Please have a seat." 

After five minutes, a woman came out and said, "Is **anyone** here for the Marketing Manager position?" Sarah stood up and shook her hand. "I'm Sarah Chen." The interviewer introduced herself: "Nice to meet you, Sarah. I'm Linda Park, the HR Director. **Everyone** is excited to meet you. Let's go to the conference room."`
    },
    {
      type: "mc",
      q: "Who greeted Sarah first at the office?",
      options: ["The receptionist", "Linda Park", "No one"],
      a: "The receptionist",
      successtip: "✓ READING DETAIL!\nThe receptionist was the first point of contact. 🏢"
    },
    {
      type: "input",
      q: "What position is Sarah applying for?",
      a: "Marketing Manager",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ CORRECT!\nMarketing Manager position. 💼"
    },
    {
      type: "mc",
      q: "Which indefinite pronoun is used for the question: 'Is ___ here for the position?'",
      options: ["anyone", "someone", "everyone"],
      a: "anyone",
      tip: "Grammar: We use 'anyone' for questions! ❓",
      successtip: "✓ GRAMMAR CHECK!\n'Anyone' is perfect for questions. 🎯"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Experience & References (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Interview Begins**

In the conference room, Linda began the interview. "So, Sarah, why do you want this job?" Sarah answered confidently: "I'm passionate about digital marketing, and I love your company's mission. I have five years of experience in social media marketing and content creation." 

Linda smiled. "That's great! What did you do at your last job?" "I managed social media campaigns for 15 clients. I also trained new staff members on our software," Sarah explained. "Excellent," Linda said. "**Who** gave you your reference?" "My previous manager, David Wong, gave me a strong recommendation," Sarah replied.`
    },
    {
      type: "mc",
      q: "How many years of experience does Sarah have?",
      options: ["Five years", "Three years", "Ten years"],
      a: "Five years",
      successtip: "✓ 5 YEARS!\nStrong professional background. 📈"
    },
    {
      type: "input",
      q: "Who gave Sarah her reference/recommendation?",
      a: "David Wong",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Her previous manager", "Her manager"] },
      successtip: "✓ DAVID WONG!\nHer previous manager. ✍️"
    },
    {
      type: "mc",
      q: "At her last job, what were Sarah's main responsibilities?",
      options: ["Managed social media and trained staff", "Only managed social media", "Only trained staff"],
      a: "Managed social media and trained staff",
      successtip: "✓ MULTIPLE TASKS!\nSocial media + training! 💻"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Logistics & Growth (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Details**

"When can you start?" Linda asked. "I can start in two weeks. I need to give notice at my current company," Sarah replied. 

Linda nodded. "**Everyone** here works part-time or full-time. This position is full-time: Monday to Friday, 9 to 5. The salary is $65,000 per year. Do you have any questions for us?" Sarah thought for a moment. "Yes, what are the growth opportunities here?" Linda smiled. "Great question! We promote from within. **No one** stays in the same position forever if they're doing great work."`
    },
    {
      type: "mc",
      q: "Is the Marketing Manager position part-time or full-time?",
      options: ["Full-time", "Part-time", "Not specified"],
      a: "Full-time",
      successtip: "✓ FULL-TIME!\nMonday to Friday, 9-5. ⏰"
    },
    {
      type: "input",
      q: "What is the annual salary offered for the position?",
      a: "65000",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["$65,000", "65,000", "65000 dollars", "65k"] },
      successtip: "✓ $65,000!\nA professional salary. 💰"
    },
    {
      type: "mc",
      q: "When is Sarah available to start the new job?",
      options: ["In two weeks", "Immediately", "Next month"],
      a: "In two weeks",
      successtip: "✓ IN TWO WEEKS!\nStandard professional notice period. 🗓️"
    },
    {
      type: "mc",
      q: "COMPREHENSION: This reading shows that the company:",
      options: ["Offers growth and values great work", "Is only hiring part-time", "Doesn't have an HR department"],
      a: "Offers growth and values great work",
      successtip: "✓ READING COMPLETE! 🎉\nYou understood:\n✓ Indefinite pronouns (someone, everyone, no one)\n✓ Subject/Object questions\n✓ Interview vocabulary!\nExcellent! 🏆"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u11-review",
    title: "Unit 11 Review: Job & Grammar Mastery",
    questions: [
      {
        type: "match",
        q: "Review job process:",
        pairs: [
          { left: "Look for a job", right: "Search" },
          { left: "Apply for a job", right: "Send resume" },
          { left: "Have an interview", right: "Meet employer" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "RESUME: 'My ___ include a business degree and marketing certificate.'",
        options: ["qualifications", "references", "hobbies"],
        a: "qualifications"
      },
      {
        type: "input",
        q: "SUBJECT QUESTION: 'Who ___ the meeting?' (organize - past)",
        a: "organized",
        accept: { ignoreCase: true, ignorePunctuation: true },
        tip: "Subject question → no 'did' → past simple"
      },
      {
        type: "mc",
        q: "OBJECT QUESTION: 'Who did you ___ yesterday?' (see - base)",
        options: ["see", "saw", "seeing"],
        a: "see",
        tip: "Object question → after 'did' → base form"
      },
      {
        type: "input",
        q: "POSITIVE: '___ is waiting for you.' (unknown person)",
        a: "Someone",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Somebody"] }
      },
      {
        type: "mc",
        q: "QUESTION: 'Did ___ call me?'",
        options: ["anyone", "someone", "everyone"],
        a: "anyone"
      },
      {
        type: "input",
        q: "ALL PEOPLE: '___ is at the meeting.' (whole group + singular verb)",
        a: "Everyone",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Everybody"] }
      },
      {
        type: "mc",
        q: "ZERO PEOPLE: '___ answered the phone.'",
        options: ["No one", "Everyone", "Someone"],
        a: "No one"
      },
      {
        type: "order",
        q: "Build full sentence:",
        words: ["I'm", "looking", "for", "a", "full-time", "job", "in", "marketing"],
        a: "I'm looking for a full-time job in marketing"
      },
      {
        type: "mc",
        q: "FINAL MASTERY: You can now: understand job ads, answer interview questions, use subject/object questions, and use indefinite pronouns! What's the trickiest?",
        options: ["Subject vs Object questions (Who called? vs Who did you call?)", "Indefinite pronouns", "Job vocabulary"],
        a: "Subject vs Object questions (Who called? vs Who did you call?)",
        successtip: "✓ CONGRATULATIONS! B1+ grammar mastered! Subject questions (no 'did'), Object questions (uses 'did'), Indefinite pronouns (someone/anyone/everyone/no one). You're ready for professional English! 🎉💼"
      }
    ]
  }
};
